/**
 * tm/pages/MobileCall.tsx — 휴대폰 /m 「통화 녹음 올리기」 (2026-10-28 지도사 「고객과 통화한 것도 업로드 · 모바일에서 간단하게 ·
 *   업로드 후 담당 팀장님에게 문자로 안내 · 통화 녹음된 폴더 찾아서 클릭해서 쉽게」)
 *  ① 녹음 고르기 — 소리 파일만(휴대폰이 최근 통화 녹음을 위에 보여 줌) · 또는 전화 앱 「공유 › 로움」(public/m-sw.js 가 받아 둔 파일)
 *     웹에서는 휴대폰의 특정 폴더를 바로 열 수 없다(브라우저가 막음) — 그래서 공유로 받는 길을 함께 둔다
 *  ② 업체 — 파일 이름의 번호(삼성 「통화 녹음 이름_01012345678_20261028102238」)로 찾음 · 팀장님 번호면 그 팀장님 업체 중에서
 *  ③ 한 줄 메모(팀장님 문자 본문) · ④ 정리되면 담당 팀장님께 문자(미리보기) · 올리기(진행률)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Company, FP } from '../types';
import { G, Icon, clamp } from '../portal-ui/tds';
import type { ByRecent } from './mobileKit';
import { Button, Check, Field, Next, Picker, SaveBar, TOSS, guard, input } from './mobileKit';
import { KEEP_NOTE, callAccept, callWhen, isCallAudio, matchCall, noticeText, showPhone, takeSharedFile, uploadCall } from '../callRec';

const MAX_MB = 300;

export default function MobileCall({ preCompany, shared, companies, fps, compSub, byRecent, toast, onCompany, onSaved }: {
  preCompany?: string; shared?: boolean; companies: Company[]; fps: FP[]; compSub: (c: Company) => string; byRecent: ByRecent;
  toast: (m: string) => void; onCompany: (id: string) => void; onSaved?: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [cid, setCid] = useState<string | null>(preCompany || null);
  const [memo, setMemo] = useState('');
  const [send, setSend] = useState(true);
  const [fpIds, setFpIds] = useState<string[]>([]);
  const [peek, setPeek] = useState(false);
  const [pct, setPct] = useState<number | null>(null);
  const [done, setDone] = useState<{ cid: string; notify: string } | null>(null);
  const inRef = useRef<HTMLInputElement | null>(null);

  // 「공유 › 로움」으로 들어왔으면 받아 둔 파일을 꺼낸다
  useEffect(() => {
    if (!shared) return;
    void takeSharedFile().then(f => { if (f) pickFile(f); else toast('공유한 파일을 찾지 못했어요 · 아래에서 골라 주세요'); });
  }, [shared]);   // eslint-disable-line react-hooks/exhaustive-deps

  const m = useMemo(() => (file ? matchCall(file.name, companies, fps) : null), [file, companies, fps]);
  const comp = companies.find(c => c.id === cid) || null;
  // 문자 받을 분 = 업체 담당 팀장님(연락처 있는 분) — 업체가 바뀌면 다시 모두
  const targets = useMemo(() => (comp ? (comp.fps || []).map(cf => { const f = fps.find(x => x.id === cf.fp_id); return { id: cf.fp_id, name: cf.name || (f ? f.name : ''), title: f ? f.title : '', phone: cf.phone || (f ? f.phone : ''), portal: !!(f && f.portal_token && f.is_active !== false) }; }) : []), [comp, fps]);
  useEffect(() => { setFpIds(targets.filter(t => t.phone && t.portal).map(t => t.id)); }, [targets]);

  function pickFile(f: File) {
    if (!isCallAudio(f)) { toast('녹음 파일(m4a · mp3 등)을 골라 주세요'); return; }
    if (f.size > MAX_MB * 1048576) { toast(`녹음 파일은 ${MAX_MB}MB 까지예요`); return; }
    setFile(f); setDone(null);
    const mm = matchCall(f.name, companies, fps);
    // 업체 화면에서 들어왔으면 그 업체 그대로 · 아니면 번호로 찾은 업체(여럿이거나 팀장님 번호면 고르게 비움)
    if (!preCompany) setCid(mm.hits.length === 1 ? mm.hits[0].company.id : null);
  }
  const fpCompanies = m && m.fp ? companies.filter(c => (c.fps || []).some(x => x.fp_id === (m.fp as FP).id)) : [];
  const pickList = m && m.hits.length > 1 ? m.hits.map(h => h.company) : fpCompanies.length ? fpCompanies : companies;
  const hit = m && comp ? m.hits.find(h => h.company.id === comp.id) : undefined;
  const notify = send && fpIds.length > 0;
  const missing = !file ? '통화 녹음을 골라 주세요' : !cid ? '업체를 고르세요' : '';

  const save = async () => {
    if (!guard(missing, pct !== null, toast) || !file || !cid || !m) return;
    setPct(0);
    try {
      const r = await uploadCall(cid, file, { memo: memo.trim(), callAt: m.at, phone: m.phone, notify, fpIds: notify ? fpIds : [] }, setPct);
      const who = targets.filter(t => fpIds.includes(t.id)).map(t => t.name).join(' · ');
      setDone({ cid, notify: r.notify ? who : '' });
      setFile(null); setMemo(''); setPeek(false);
      toast('올렸어요 · 대화 글로 정리하고 있어요');
      if (onSaved) onSaved();
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '올리지 못했어요'); }
    finally { setPct(null); }
  };

  if (done) {
    const c = companies.find(x => x.id === done.cid);
    return (
      <>
        <div style={{ padding: '24px 0 4px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 28, background: TOSS.blueLt, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={28} color={TOSS.blue} sw={2.4} /></div>
          <div style={{ fontSize: 20, fontWeight: 700, color: TOSS.text, marginTop: 14 }}>올렸어요</div>
          <div style={{ fontSize: 16, color: TOSS.sub, marginTop: 6, lineHeight: 1.6, wordBreak: 'keep-all' }}>
            {c ? `${c.name} · ` : ''}녹음 1시간에 15분쯤이면 대화 글로 정리돼요(10분 통화는 3분쯤).
            {done.notify ? <><br />정리가 끝나면 {done.notify} 팀장님께 문자가 가요.</> : null}
          </div>
        </div>
        <Next items={[{ t: '업체 화면에서 보기', on: () => onCompany(done.cid) }, { t: '다른 통화 녹음 올리기', on: () => { setDone(null); setCid(preCompany || null); } }]} />
      </>
    );
  }

  return (
    <>
      <input ref={inRef} type="file" accept={callAccept()} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) pickFile(f); }} />
      <Field title="통화 녹음" need>
        {!file ? (
          <>
            <button type="button" className="tds-btn" onClick={() => inRef.current && inRef.current.click()}
              style={{ width: '100%', minHeight: 96, borderRadius: 16, border: `1.5px dashed ${TOSS.blue}`, background: TOSS.blueLt, color: TOSS.blueDk, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Icon name="mic" size={28} color={TOSS.blue} sw={2} />
              <span style={{ fontSize: 17, fontWeight: 700 }}>통화 녹음 고르기</span>
            </button>
            <div style={{ fontSize: 15, color: TOSS.sub, marginTop: 8, lineHeight: 1.55, wordBreak: 'keep-all' }}>
              처음 한 번 <b style={{ color: TOSS.text }}>내장 저장공간 › Recordings › Call</b> 폴더를 열어 두면 다음부터 그 폴더가 먼저 떠요<br />
              전화 앱 통화 녹음에서 <b style={{ color: TOSS.text }}>공유 › 로움</b>으로도 바로 올릴 수 있어요(이 화면을 홈 화면에 추가해 두면 공유 목록에 떠요)<br />{KEEP_NOTE}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 64, padding: '8px 6px 8px 14px', borderRadius: 14, background: '#fff', border: `1px solid ${G.g300}` }}>
            <Icon name="mic" size={22} color={TOSS.blue} sw={2} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{m && m.at ? callWhen(m.at) : file.name}</div>
              <div style={{ fontSize: 15, color: TOSS.sub, fontVariantNumeric: 'tabular-nums', ...clamp(1) }}>{[m && m.who, m && m.phone ? showPhone(m.phone) : '', `${(file.size / 1048576).toFixed(1)}MB`].filter(Boolean).join(' · ')}</div>
            </div>
            <button type="button" className="tds-btn" onClick={() => inRef.current && inRef.current.click()} style={{ minHeight: 44, padding: '0 14px', border: 'none', background: 'none', color: TOSS.blue, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>바꾸기</button>
          </div>
        )}
      </Field>

      {file && m && (m.hits.length > 0 || m.fp) && (
        <div style={{ fontSize: 15, color: TOSS.blueDk, margin: '8px 0 0', lineHeight: 1.55 }}>
          {hit ? `번호로 찾았어요 · ${hit.role} ${hit.name || ''}`.trim()
            : m.hits.length > 1 ? `이 번호를 쓰는 업체가 ${m.hits.length}곳이에요 · 골라 주세요`
              : m.fp ? `${m.fp.name} 팀장님 번호예요 · 팀장님 업체에서 골라 주세요` : ''}
        </div>
      )}
      <Field title="업체" need>
        <Picker items={pickList} value={cid} onChange={id => setCid(id)} sub={compSub} placeholder="업체 이름으로 찾기" recent={byRecent} />
      </Field>

      <Field title="한 줄 메모" note="내 화면과 팀장님 포털에 녹음과 함께 보여요 · 문자에는 정해진 안내 글이 가요">
        <textarea value={memo} onChange={e => setMemo(e.target.value)} rows={3} maxLength={1000} placeholder="예) 폐업 지원금 문의 · 지점장님께 연락 부탁"
          style={{ ...input, height: 'auto', minHeight: 96, padding: '12px 14px', lineHeight: 1.55, resize: 'vertical' }} />
      </Field>

      {comp && (
        <Field title="담당 팀장님께 문자">
          {targets.length === 0 ? <div style={{ fontSize: 15, color: TOSS.sub }}>이 업체에 담당 팀장님이 없어요 · 녹음만 저장해요</div> : (<>
            <Check on={send} onChange={setSend}>대화 글로 정리되면 문자 보내기</Check>
            {send && (
              <div style={{ display: 'grid', gap: 8 }}>
                {targets.map(t => {
                  const can = !!t.phone && t.portal, on = fpIds.includes(t.id);
                  return (
                    <button key={t.id} type="button" className="tds-btn" disabled={!can} aria-pressed={on} onClick={() => setFpIds(v => (on ? v.filter(x => x !== t.id) : [...v, t.id]))}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 56, padding: '0 14px', borderRadius: 14, border: `${on ? 2 : 1}px solid ${on ? TOSS.blue : G.g300}`, background: on ? TOSS.blueLt : '#fff', cursor: can ? 'pointer' : 'default', fontFamily: 'inherit', textAlign: 'left', opacity: can ? 1 : 0.6 }}>
                      <span style={{ width: 22, fontSize: 17, color: TOSS.blue, fontWeight: 800 }}>{on ? '✓' : ''}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: on ? 700 : 500, color: TOSS.text, ...clamp(1) }}>{t.name} {t.title || '팀장'}님</span>
                      <span style={{ flexShrink: 0, fontSize: 15, color: TOSS.sub, fontVariantNumeric: 'tabular-nums' }}>{!t.phone ? '연락처 없음' : !t.portal ? '포털 주소 없음' : showPhone(t.phone)}</span>
                    </button>
                  );
                })}
                <button type="button" className="tds-btn" onClick={() => setPeek(v => !v)} aria-expanded={peek}
                  style={{ justifySelf: 'start', minHeight: 44, padding: 0, border: 'none', background: 'none', color: TOSS.blue, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{peek ? '미리보기 닫기' : '문자 미리보기'}</button>
                {peek && targets.filter(t => fpIds.includes(t.id)).slice(0, 1).map(t => (
                  <div key={t.id} style={{ padding: '12px 14px', borderRadius: 14, background: TOSS.bg, fontSize: 15, color: TOSS.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{noticeText(comp.name, t)}</div>
                ))}
              </div>
            )}
          </>)}
        </Field>
      )}
      <div style={{ height: 96 }} />
      <SaveBar>
        <Button onClick={() => void save()} muted={!!missing}>{pct !== null ? `올리는 중 ${pct}%` : notify ? '올리고 정리되면 문자 보내기' : '올리기'}</Button>
      </SaveBar>
    </>
  );
}
